<template>
  <div v-if="isOpen" class="fixed inset-0 z-[1000] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" @click.self="close">
    <div class="bg-white dark:bg-slate-850 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 max-w-md w-full overflow-hidden flex flex-col max-h-[90vh] animate-in fade-in zoom-in-95 duration-150">
      
      <!-- Header -->
      <div class="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-700">
        <div class="flex items-center gap-3">
          <div class="p-2 bg-indigo-50 dark:bg-indigo-950/60 rounded-xl text-indigo-600 dark:text-indigo-400">
            <RadioIcon class="w-6 h-6" />
          </div>
          <div>
            <h3 class="text-base font-bold text-gray-900 dark:text-white">
              {{ $t('presentation.syncTitle') || 'Presentation Sync & Remote' }}
            </h3>
            <p class="text-xs text-gray-500 dark:text-gray-400">
              {{ $t('presentation.syncSubtitle') || 'Connect remote controllers and presentation screens' }}
            </p>
          </div>
        </div>
        <button @click="close" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 cursor-pointer">
          <XMarkIcon class="w-5 h-5" />
        </button>
      </div>

      <!-- Mode Selector Tabs -->
      <div class="grid grid-cols-2 p-3 gap-2 bg-gray-50 dark:bg-slate-900 border-b border-gray-200 dark:border-gray-800">
        <button 
          @click="selectMode('cloud')"
          :class="currentMode === 'cloud' ? 'bg-white dark:bg-slate-800 text-indigo-600 dark:text-indigo-400 shadow-sm font-semibold' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 font-medium'"
          class="flex items-center justify-center gap-2 py-2 px-3 rounded-xl text-xs transition-all duration-150 cursor-pointer">
          <CloudIcon class="w-4 h-4" />
          <span>{{ $t('stageSync.cloudMode') || 'Cloud' }}</span>
        </button>
        <button 
          @click="selectMode('local')"
          :class="currentMode === 'local' ? 'bg-white dark:bg-slate-800 text-indigo-600 dark:text-indigo-400 shadow-sm font-semibold' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 font-medium'"
          class="flex items-center justify-center gap-2 py-2 px-3 rounded-xl text-xs transition-all duration-150 cursor-pointer">
          <WifiIcon class="w-4 h-4" />
          <span>{{ $t('stageSync.localMode') || 'Local Stage (4-Digit PIN)' }}</span>
        </button>
      </div>

      <!-- Modal Body -->
      <div class="p-6 overflow-y-auto flex-1 space-y-5">
        
        <!-- Cloud Mode Explanation -->
        <div v-if="currentMode === 'cloud'" class="space-y-4 text-center py-2">
          <div class="w-14 h-14 mx-auto rounded-full bg-indigo-50 dark:bg-indigo-950/50 flex items-center justify-center text-indigo-600 dark:text-indigo-400">
            <CloudIcon class="w-8 h-8" />
          </div>
          <div>
            <h4 class="text-sm font-semibold text-gray-900 dark:text-white">{{ $t('stageSync.cloudActiveTitle') || 'Cloud Mode Active' }}</h4>
            <p class="text-xs text-gray-500 dark:text-gray-400 mt-1 max-w-sm mx-auto">
              {{ $t('stageSync.cloudActiveDesc') || 'Uses internet to automatically synchronize presentation screens and controllers in real-time.' }}
            </p>
          </div>
          <div class="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium bg-emerald-50 dark:bg-emerald-950/50 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800">
            <span class="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
            {{ $t('stageSync.cloudReadyBadge') || 'Ready for presentations' }}
          </div>
          
          <!-- QR Connect for Cloud -->
          <div class="pt-2 border-t border-gray-100 dark:border-gray-800 text-left">
            <div class="flex items-center justify-between mb-2">
              <span class="text-xs font-bold text-gray-800 dark:text-gray-200 uppercase tracking-wide">
                {{ $t('presentation.qrAccessTitle') || 'Quick Mobile / Screen Connect' }}
              </span>
              <div class="flex items-center gap-1 bg-gray-100 dark:bg-slate-800 p-0.5 rounded-lg text-[11px]">
                <button 
                  @click="qrTarget = 'remote'"
                  :class="qrTarget === 'remote' ? 'bg-white dark:bg-slate-900 font-bold shadow-xs text-indigo-600 dark:text-indigo-400' : 'text-gray-500 hover:text-gray-800 dark:text-gray-400'"
                  class="px-2 py-0.5 rounded-md transition-all cursor-pointer">
                  📱 {{ $t('presentation.remoteCtrl') || 'Remote' }}
                </button>
                <button 
                  @click="qrTarget = 'viewer'"
                  :class="qrTarget === 'viewer' ? 'bg-white dark:bg-slate-900 font-bold shadow-xs text-indigo-600 dark:text-indigo-400' : 'text-gray-500 hover:text-gray-800 dark:text-gray-400'"
                  class="px-2 py-0.5 rounded-md transition-all cursor-pointer">
                  📺 {{ $t('presentation.viewerScreen') || 'Screen' }}
                </button>
              </div>
            </div>

            <div class="flex flex-col sm:flex-row items-center gap-3 bg-gray-50 dark:bg-slate-900 p-3.5 rounded-xl border border-gray-200 dark:border-gray-800">
              <div class="bg-white p-2 rounded-lg shadow-sm shrink-0">
                <QRCodeVue3 
                  :value="targetQrUrl"
                  :width="110"
                  :height="110"
                  :dotsOptions="{ type: 'rounded', color: '#1e1b4b' }"
                  :cornersSquareOptions="{ type: 'extra-rounded', color: '#4338ca' }"
                />
              </div>
              <div class="flex-1 space-y-1.5 text-center sm:text-left w-full">
                <h5 class="text-xs font-bold text-gray-900 dark:text-white">
                  {{ qrTarget === 'remote' ? ($t('presentation.scanForRemote') || 'Scan with phone for Remote Control') : ($t('presentation.scanForViewer') || 'Scan or open Presentation Display') }}
                </h5>
                <p class="text-[11px] text-gray-500 dark:text-gray-400 leading-tight">
                  {{ qrTarget === 'remote' ? ($t('presentation.remoteDesc') || 'Control slides, blank screen, switch songs and logo.') : ($t('presentation.viewerDesc') || 'Display lyrics and media fullscreen on projector or TV.') }}
                </p>
                <div class="flex flex-wrap gap-2 pt-1">
                  <button
                    @click="openTargetUrl"
                    class="px-2.5 py-1 bg-indigo-50 dark:bg-indigo-950/60 hover:bg-indigo-100 text-indigo-600 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-800 rounded-lg text-xs font-semibold flex items-center gap-1 cursor-pointer">
                    <ArrowTopRightOnSquareIcon class="w-3.5 h-3.5" />
                    <span>{{ $t('presentation.openInNewTab') || 'Open' }}</span>
                  </button>
                  <button
                    @click="copy(targetQrUrl)"
                    class="px-2.5 py-1 bg-white dark:bg-slate-800 hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-700 rounded-lg text-xs font-semibold flex items-center gap-1 cursor-pointer">
                    <ClipboardDocumentIcon class="w-3.5 h-3.5 text-gray-400" />
                    <span>{{ $t('presentation.copyLink') || 'Copy Link' }}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Local WebRTC Mode with Subtabs: Host vs Join -->
        <div v-else class="space-y-4">
          
          <!-- Sub-tab Role Selector: Host vs Guest -->
          <div class="flex items-center justify-center gap-2 p-1 bg-gray-100 dark:bg-slate-900 rounded-xl text-xs font-medium">
            <button 
              @click="localRole = 'host'" 
              :class="localRole === 'host' ? 'bg-white dark:bg-slate-800 shadow-sm text-indigo-600 dark:text-indigo-400 font-bold' : 'text-gray-500 hover:text-gray-800 dark:text-gray-400'"
              class="flex-1 py-1.5 rounded-lg transition-all cursor-pointer">
              {{ $t('stageSync.roleHost') || 'Create Session (Leader)' }}
            </button>
            <button 
              @click="localRole = 'guest'" 
              :class="localRole === 'guest' ? 'bg-white dark:bg-slate-800 shadow-sm text-indigo-600 dark:text-indigo-400 font-bold' : 'text-gray-500 hover:text-gray-800 dark:text-gray-400'"
              class="flex-1 py-1.5 rounded-lg transition-all cursor-pointer">
              {{ $t('stageSync.roleGuest') || 'Enter PIN (Musician)' }}
            </button>
          </div>

          <!-- LÍDER / HOST VIEW -->
          <div v-if="localRole === 'host'" class="space-y-4 text-center">
            <p class="text-xs text-gray-500 dark:text-gray-400" v-html="$t('stageSync.hostHelp') || 'Connect devices on the <strong>same Wi-Fi network</strong>.'"></p>

            <!-- PIN Displayed -->
            <div class="space-y-3">
              <span class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                {{ $t('stageSync.pinLabel') || 'SESSION PIN' }}
              </span>
              
              <div class="flex items-center justify-center gap-2 my-1">
                <div v-for="(digit, idx) in formattedPin" :key="idx" 
                  class="w-12 h-14 bg-indigo-50 dark:bg-indigo-950/60 border-2 border-indigo-500/40 rounded-xl flex items-center justify-center text-2xl font-black font-mono text-indigo-600 dark:text-indigo-400 shadow-sm">
                  {{ digit }}
                </div>
              </div>

              <p class="text-xs text-gray-600 dark:text-gray-300">
                {{ $t('presentation.pinHelp') || 'Use this 4-digit code in the Presentation app or mobile browser to sync instantly.' }}
              </p>

              <!-- Local Network Host Controls -->
              <div class="pt-1 flex flex-wrap gap-2 justify-center">
                <button
                  v-if="!isHostActive"
                  @click="startHostSession"
                  :disabled="isStartingHost"
                  class="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold rounded-xl shadow-md transition-all flex items-center gap-2 cursor-pointer disabled:opacity-50">
                  <ArrowPathIcon v-if="isStartingHost" class="w-4 h-4 animate-spin" />
                  <SparklesIcon v-else class="w-4 h-4" />
                  <span>{{ isStartingHost ? ($t('stageSync.generatingPin') || 'Starting...') : ($t('presentation.startWebRtcHost') || 'Enable Local Network (LAN)') }}</span>
                </button>
                <button
                  v-else
                  @click="disconnectWebRTC"
                  class="px-3.5 py-2 border border-red-300 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40 text-xs font-semibold rounded-xl transition-all flex items-center gap-1.5 cursor-pointer">
                  <XMarkIcon class="w-4 h-4" />
                  <span>{{ $t('presentation.stopHost') || 'Disconnect Local Network' }}</span>
                </button>
                <button
                  @click="copy(activeRoomId)"
                  class="px-3.5 py-2 bg-white dark:bg-slate-800 hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-700 text-xs font-semibold rounded-xl transition-all flex items-center gap-1.5 cursor-pointer">
                  <ClipboardDocumentIcon class="w-4 h-4 text-gray-400" />
                  <span>{{ $t('presentation.copyPin') || 'Copy Code' }}</span>
                </button>
              </div>

              <!-- Connected peers count -->
              <div class="p-3 bg-gray-50 dark:bg-slate-900 rounded-xl flex items-center justify-between text-xs border border-gray-200 dark:border-gray-800">
                <span class="text-gray-600 dark:text-gray-400 font-medium">{{ $t('stageSync.connectedMusicians') || 'Connected Devices:' }}</span>
                <span class="font-bold text-sm text-indigo-600 dark:text-indigo-400">{{ connectedCount }}</span>
              </div>
            </div>

            <!-- Quick Mobile / Screen QR Connect Section -->
            <div class="pt-2 border-t border-gray-100 dark:border-gray-800 text-left">
              <div class="flex items-center justify-between mb-2">
                <span class="text-xs font-bold text-gray-800 dark:text-gray-200 uppercase tracking-wide">
                  {{ $t('presentation.qrAccessTitle') || 'Quick Mobile / Screen Connect' }}
                </span>
                <div class="flex items-center gap-1 bg-gray-100 dark:bg-slate-800 p-0.5 rounded-lg text-[11px]">
                  <button 
                    @click="qrTarget = 'remote'"
                    :class="qrTarget === 'remote' ? 'bg-white dark:bg-slate-900 font-bold shadow-xs text-indigo-600 dark:text-indigo-400' : 'text-gray-500 hover:text-gray-800 dark:text-gray-400'"
                    class="px-2 py-0.5 rounded-md transition-all cursor-pointer">
                    📱 {{ $t('presentation.remoteCtrl') || 'Remote' }}
                  </button>
                  <button 
                    @click="qrTarget = 'viewer'"
                    :class="qrTarget === 'viewer' ? 'bg-white dark:bg-slate-900 font-bold shadow-xs text-indigo-600 dark:text-indigo-400' : 'text-gray-500 hover:text-gray-800 dark:text-gray-400'"
                    class="px-2 py-0.5 rounded-md transition-all cursor-pointer">
                    📺 {{ $t('presentation.viewerScreen') || 'Screen' }}
                  </button>
                </div>
              </div>

              <div class="flex flex-col sm:flex-row items-center gap-3 bg-gray-50 dark:bg-slate-900 p-3.5 rounded-xl border border-gray-200 dark:border-gray-800">
                <div class="bg-white p-2 rounded-lg shadow-sm shrink-0">
                  <QRCodeVue3 
                    :value="targetQrUrl"
                    :width="110"
                    :height="110"
                    :dotsOptions="{ type: 'rounded', color: '#1e1b4b' }"
                    :cornersSquareOptions="{ type: 'extra-rounded', color: '#4338ca' }"
                  />
                </div>
                <div class="flex-1 space-y-1.5 text-center sm:text-left w-full">
                  <h5 class="text-xs font-bold text-gray-900 dark:text-white">
                    {{ qrTarget === 'remote' ? ($t('presentation.scanForRemote') || 'Scan with phone for Remote Control') : ($t('presentation.scanForViewer') || 'Scan or open Presentation Display') }}
                  </h5>
                  <p class="text-[11px] text-gray-500 dark:text-gray-400 leading-tight">
                    {{ qrTarget === 'remote' ? ($t('presentation.remoteDesc') || 'Control slides, blank screen, switch songs and logo.') : ($t('presentation.viewerDesc') || 'Display lyrics and media fullscreen on projector or TV.') }}
                  </p>
                  <div class="flex flex-wrap gap-2 pt-1">
                    <button
                      @click="openTargetUrl"
                      class="px-2.5 py-1 bg-indigo-50 dark:bg-indigo-950/60 hover:bg-indigo-100 text-indigo-600 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-800 rounded-lg text-xs font-semibold flex items-center gap-1 cursor-pointer">
                      <ArrowTopRightOnSquareIcon class="w-3.5 h-3.5" />
                      <span>{{ $t('presentation.openInNewTab') || 'Open' }}</span>
                    </button>
                    <button
                      @click="copy(targetQrUrl)"
                      class="px-2.5 py-1 bg-white dark:bg-slate-800 hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-700 rounded-lg text-xs font-semibold flex items-center gap-1 cursor-pointer">
                      <ClipboardDocumentIcon class="w-3.5 h-3.5 text-gray-400" />
                      <span>{{ $t('presentation.copyLink') || 'Copy Link' }}</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- MÚSICO / GUEST VIEW (EXACT PLAYLIST STANDARD) -->
          <div v-else class="space-y-4 text-center">
            <p class="text-xs text-gray-500 dark:text-gray-400">
              {{ $t('stageSync.guestHelp') || 'Enter the 4-digit PIN provided by the presenter or band leader:' }}
            </p>

            <div class="flex items-center justify-center gap-2 my-2">
              <input 
                v-model="inputPin"
                type="text" 
                maxlength="6" 
                placeholder="PIN"
                class="w-56 h-14 text-center tracking-[0.25em] text-2xl sm:text-3xl font-black font-mono bg-gray-50 dark:bg-slate-900 border-2 border-indigo-400 focus:border-indigo-600 rounded-xl focus:ring-0 focus:outline-none dark:text-white"
              />
            </div>

            <button 
              @click="joinSessionByPin"
              :disabled="inputPin.trim().length < 4 || isJoining"
              class="w-full py-3 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold rounded-xl shadow-md transition-all flex items-center justify-center gap-2 disabled:opacity-50 cursor-pointer">
              <ArrowPathIcon v-if="isJoining" class="w-4 h-4 animate-spin" />
              <span>{{ isJoining ? ($t('stageSync.connecting') || 'Connecting...') : ($t('stageSync.connectBtn') || 'Connect to Stage') }}</span>
            </button>

            <div v-if="connectedCount > 0" class="p-3 bg-emerald-50 dark:bg-emerald-950/50 rounded-xl flex items-center justify-center gap-2 text-xs text-emerald-700 dark:text-emerald-400 font-semibold border border-emerald-200 dark:border-emerald-800">
              <CheckCircleIcon class="w-4 h-4 text-emerald-500" />
              <span>{{ $t('stageSync.connectedSuccess') || 'Connected successfully!' }}</span>
            </div>

            <button
              v-if="connectedCount > 0"
              @click="disconnectWebRTC"
              class="w-full py-2.5 border border-red-300 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40 text-xs font-semibold rounded-xl transition-all flex items-center justify-center gap-2 cursor-pointer">
              <XMarkIcon class="w-4 h-4" />
              <span>{{ $t('stageSync.disconnectBtn') || 'Disconnect from Stage' }}</span>
            </button>
          </div>

        </div>

      </div>

      <!-- Footer -->
      <div class="px-6 py-3 bg-gray-50 dark:bg-slate-900/50 border-t border-gray-100 dark:border-gray-800 flex justify-end">
        <button @click="close" class="px-4 py-2 text-xs font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-slate-700 rounded-xl transition-all cursor-pointer">
          {{ $t('stageSync.close') || 'Close' }}
        </button>
      </div>

    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, defineProps, defineEmits, defineExpose, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useClipboard } from '@vueuse/core';
import QRCodeVue3 from "qrcode-vue3";
import { 
  XMarkIcon, RadioIcon, CloudIcon, WifiIcon, 
  SparklesIcon, ArrowPathIcon, ClipboardDocumentIcon,
  ArrowTopRightOnSquareIcon, CheckCircleIcon 
} from '@heroicons/vue/24/outline';
import transportManager from '@/service/realtime/transport.manager';
import toastStore from '@/store/toast.store';
import { getPresentationViewerUrl, openPresentationWindow } from '@/utils/presentationUrl';

const props = defineProps({
  roomId: {
    type: String,
    required: true
  },
  playlistId: {
    type: String,
    default: ''
  }
});

const emit = defineEmits(['update:roomId', 'room-changed']);

const { t } = useI18n();
const { copy: copyToClipboard } = useClipboard();

const isOpen = ref(false);
const currentMode = ref(transportManager.getMode());
const localRole = ref('host'); // 'host' | 'guest'
const activeRoomId = ref(props.roomId || '');
const qrTarget = ref('remote'); // 'remote' | 'viewer'

const isHostActive = ref(false);
const isStartingHost = ref(false);
const connectedCount = ref(0);

const isJoining = ref(false);
const inputPin = ref('');

let pollAnswersInterval = null;
let countInterval = null;

watch(() => props.roomId, (newVal) => {
  if (newVal) {
    activeRoomId.value = newVal;
  }
});

const formattedPin = computed(() => {
  const clean = String(activeRoomId.value || '').trim();
  if (clean.length === 4) {
    return clean.split('');
  }
  if (clean.length > 4) {
    return clean.substring(0, 4).split('');
  }
  return (clean.padEnd(4, '•')).split('');
});

const getViewerUrl = () => {
  return getPresentationViewerUrl(activeRoomId.value);
};

const getRemoteControllerUrl = () => {
  const base = `${window.location.protocol}//${window.location.host}`;
  const pid = props.playlistId || 'all';
  return `${base}/#/playlist/${pid}/presentation/remote?room=${encodeURIComponent(activeRoomId.value)}&mode=remote`;
};

const targetQrUrl = computed(() => {
  return qrTarget.value === 'remote' ? getRemoteControllerUrl() : getViewerUrl();
});

const openTargetUrl = () => {
  if (qrTarget.value === 'viewer') {
    openPresentationWindow(targetQrUrl.value);
  } else {
    window.open(targetQrUrl.value, '_blank');
  }
};

const copy = (text) => {
  copyToClipboard(text);
  toastStore.dispatch('addMessage', {
    message: t('presentation.copied') || 'Copied to clipboard!',
    icon: 'success',
    ttl: 2500
  });
};

const selectMode = (mode) => {
  currentMode.value = mode;
  transportManager.setMode(mode);
  toastStore.dispatch('addMessage', {
    message: mode === 'cloud' 
      ? (t('stageSync.toastCloudActivated') || 'Cloud Mode Activated') 
      : (t('stageSync.toastLocalActivated') || 'Local LAN Mode Activated'),
    icon: 'info',
    ttl: 3000
  });
};

const startHostSession = async () => {
  isStartingHost.value = true;
  try {
    const webrtc = transportManager.getWebRTC();
    const customPin = /^\d{4}$/.test(activeRoomId.value) ? activeRoomId.value : null;
    const pin = await webrtc.startHostPinSession(customPin);
    activeRoomId.value = pin;
    isHostActive.value = true;
    emit('update:roomId', pin);
    emit('room-changed', pin);
    
    toastStore.dispatch('addMessage', {
      message: `${t('stageSync.toastSessionStarted', { pin }) || 'Session started! PIN:'} ${pin}`,
      icon: 'success',
      ttl: 4000
    });

    if (pollAnswersInterval) clearInterval(pollAnswersInterval);
    pollAnswersInterval = setInterval(async () => {
      await webrtc.pollHostAnswers();
      updatePeerCount();
    }, 1500);

  } catch (err) {
    console.error('Error starting WebRTC host:', err);
    toastStore.dispatch('addMessage', {
      message: err.message || t('stageSync.toastGeneratePinError') || 'Error starting WebRTC session',
      icon: 'error'
    });
  } finally {
    isStartingHost.value = false;
  }
};

const disconnectWebRTC = () => {
  try {
    if (pollAnswersInterval) {
      clearInterval(pollAnswersInterval);
      pollAnswersInterval = null;
    }
    transportManager.getWebRTC().disconnect();
    isHostActive.value = false;
    connectedCount.value = 0;
    toastStore.dispatch('addMessage', {
      message: t('stageSync.toastDisconnected') || 'Disconnected from stage.',
      icon: 'info',
      ttl: 2500
    });
  } catch (err) {
    console.warn('Error disconnecting WebRTC:', err);
  }
};

const joinSessionByPin = async () => {
  if (inputPin.value.length < 4) return;
  isJoining.value = true;
  try {
    const pin = inputPin.value.trim();
    const webrtc = transportManager.getWebRTC();
    await webrtc.joinByPin(pin);
    activeRoomId.value = pin;
    emit('update:roomId', pin);
    emit('room-changed', pin);
    toastStore.dispatch('addMessage', { 
      message: t('stageSync.toastConnectedLeader') || 'Connected to leader session!', 
      icon: 'success' 
    });
    updatePeerCount();
  } catch (err) {
    console.error('Error connecting to room by PIN:', err);
    toastStore.dispatch('addMessage', {
      message: err.message || t('stageSync.toastConnectError') || 'Error connecting to room.',
      icon: 'error'
    });
  } finally {
    isJoining.value = false;
  }
};

const updatePeerCount = () => {
  try {
    connectedCount.value = transportManager.getWebRTC().getConnectedCount();
    if (transportManager.getWebRTC().role === 'host') {
      isHostActive.value = true;
    }
  } catch (e) {
    connectedCount.value = 0;
  }
};

const open = () => {
  currentMode.value = transportManager.getMode();
  updatePeerCount();
  isOpen.value = true;
};

const close = () => {
  isOpen.value = false;
};

onMounted(() => {
  countInterval = setInterval(updatePeerCount, 2000);
});

onUnmounted(() => {
  if (countInterval) clearInterval(countInterval);
  if (pollAnswersInterval) clearInterval(pollAnswersInterval);
});

defineExpose({ open, close, startHostSession });
</script>
